import { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import {
  ZephrUserProfile,
  ZephrNewsletters,
  ZephrCustomFieldData,
} from "../zephr/profile-types";
import { getZephrUserProfile } from "../fetchers/zephr/customFields";

/**
 * Custom fields and newsletter subscription state management hook.
 *
 * This hook manages user profile data from Zephr, including newsletter preferences,
 * and provides an optimistic update function for immediate UI feedback.
 */

/**
 * Data structure returned by useCustomFields hook.
 *
 * @property customFields - Complete Zephr user profile including all custom fields
 *                         (personal info, company data, newsletter preferences, etc.)
 * @property newsletters - Subset of customFields containing only newsletter subscription states
 * @property updateNewsletter - Function to optimistically update newsletter subscription state
 *                             in both customFields and newsletters objects simultaneously
 */
export type CustomFieldHookData = {
  customFields: ZephrUserProfile;
  newsletters: ZephrNewsletters;
  updateNewsletter: (newsletterKey: string, value: boolean) => void;
};

export type UseCustomFieldsReturn = CustomFieldHookData;

/**
 * Hook for accessing and managing Zephr user profile custom fields.
 *
 * This hook fetches user profile data from Zephr (using the blaize_session cookie)
 * and provides both the complete custom fields object and a separated newsletters object.
 * It also exposes an `updateNewsletter` function for optimistic UI updates.
 *
 * **Key Features:**
 * - Fetches user profile data from Zephr on mount (when blaize_session cookie exists)
 * - Extracts newsletter preferences into a separate object for convenience
 * - Provides optimistic update function for instant UI feedback
 * - Gracefully handles API errors without crashing
 *
 * **Optimistic Updates:**
 * The `updateNewsletter` function enables the optimistic update pattern used by other hooks
 * (e.g., useMappedCallout, useMappedNewsletters). It immediately updates local state before
 * the API call completes, providing instant visual feedback to users. Other hooks are
 * responsible for API calls and state reversion on failure.
 *
 * **State Structure:**
 * - `customFields`: Full Zephr profile (all fields including newsletters)
 * - `newsletters`: Only newsletter subscription fields (wants_main_newsletter, would-like-* fields)
 * - `updateNewsletter`: Updates BOTH objects simultaneously to maintain consistency
 *
 * @returns {CustomFieldHookData} Object containing:
 * - `customFields`: Complete user profile from Zephr
 * - `newsletters`: Newsletter subscription states only
 * - `updateNewsletter(key, value)`: Function to update newsletter subscription state
 *
 * @example
 * ```tsx
 * const { customFields, newsletters, updateNewsletter } = useCustomFields();
 *
 * // Check subscription status
 * const isSubscribed = newsletters.wants_main_newsletter;
 *
 * // Optimistically update (used by subscription hooks)
 * updateNewsletter('wants_main_newsletter', true);
 *
 * // Access other profile data
 * const userEmail = customFields.email;
 * const firstName = customFields.first_name;
 * ```
 */
export function useCustomFields(): CustomFieldHookData {
  const [customFieldData, setCustomFieldData] = useState<ZephrCustomFieldData>({
    customFields: {},
    newsletters: {},
  });
  // Changed from __utp to blaize_session cookie for Zephr
  const [cookies] = useCookies(["blaize_session"]);
  const userCookie = cookies.blaize_session;

  useEffect(() => {
    let cancelled = false;
    const getCustomFieldData = async (loginCookie: string) => {
      try {
        const profileData = await getZephrUserProfile(loginCookie);

        if (!cancelled && profileData) {
          // Extract newsletter preferences
          const newsletters: ZephrNewsletters = {
            wants_main_newsletter: profileData.wants_main_newsletter,
            "would-like-member-newsletter":
              profileData["would-like-member-newsletter"],
            "would-like-futureproof-newsletter":
              profileData["would-like-futureproof-newsletter"],
            "would-like-sustainability-newsletter":
              profileData["would-like-sustainability-newsletter"],
            "would-like-startup-life-newsletter":
              profileData["would-like-startup-life-newsletter"],
            "would-like-fintech-newsletter":
              profileData["would-like-fintech-newsletter"],
            "would-like-deeptech-newsletter":
              profileData["would-like-deeptech-newsletter"],
            "would-like-scoop-newsletter":
              profileData["would-like-scoop-newsletter"],
          };

          setCustomFieldData({
            customFields: profileData,
            newsletters,
          });
        }
      } catch (err: any) {
        if (cancelled) return;
        // Soft-fail: leave defaults and optionally log (avoid crashing render)
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn(
            "Failed to load Zephr custom fields",
            err?.message || err
          );
        }
      }
    };

    if (userCookie) {
      getCustomFieldData(userCookie);
    }
    return () => {
      cancelled = true;
    };
  }, [userCookie]);

  /**
   * Updates newsletter subscription state in both customFields and newsletters objects.
   *
   * This function enables the optimistic update pattern: it immediately updates local state
   * without waiting for an API call. Consuming hooks (useMappedCallout, useMappedNewsletters)
   * call this function before their API requests to provide instant visual feedback, then
   * revert the state if the API call fails.
   *
   * **Important:** This function only updates local React state. It does NOT make API calls
   * to Zephr. The calling code is responsible for:
   * 1. Calling this function for optimistic update
   * 2. Making the API call to persist the change
   * 3. Calling this function again to revert if API fails
   *
   * @param newsletterKey - The newsletter field key (e.g., 'wants_main_newsletter')
   * @param value - The new subscription state (true = subscribed, false = unsubscribed)
   *
   * @example
   * ```tsx
   * // Optimistic update pattern (used by useMappedCallout)
   * updateNewsletter('wants_main_newsletter', true); // Instant UI update
   *
   * try {
   *   const result = await updateZephrUserProfile({ wants_main_newsletter: true });
   *   if (!result.success) {
   *     updateNewsletter('wants_main_newsletter', false); // Revert on failure
   *   }
   * } catch (error) {
   *   updateNewsletter('wants_main_newsletter', false); // Revert on error
   * }
   * ```
   */
  const updateNewsletter = (newsletterKey: string, value: boolean) => {
    setCustomFieldData((prev) => ({
      ...prev,
      newsletters: {
        ...prev.newsletters,
        [newsletterKey]: value,
      },
      customFields: {
        ...prev.customFields,
        [newsletterKey]: value,
      },
    }));
  };

  return {
    customFields: customFieldData.customFields,
    newsletters: customFieldData.newsletters,
    updateNewsletter,
  };
}
